import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  QrCode, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Camera,
  Keyboard,
  RotateCcw,
  Ticket,
  Calendar,
  MapPin,
  User,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminValidatePage() {
  const { token } = useAuth();
  const [mode, setMode] = useState('manual'); // 'manual' or 'camera'
  const [ticketInput, setTicketInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentValidations, setRecentValidations] = useState([]);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    if (html5QrCodeRef.current) {
      stopScanner();
    }

    try {
      const html5QrCode = new Html5Qrcode("admin-qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          if (navigator.vibrate) {
            navigator.vibrate(200);
          }
          handleValidate(decodedText);
          stopScanner();
        },
        () => {}
      );
      setScanning(true);
    } catch (error) {
      console.error('Failed to start scanner:', error);
      toast.error('Could not access camera. Please use manual entry.');
      setMode('manual');
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleValidate = async (ticketIdOrQr) => {
    if (!ticketIdOrQr.trim()) {
      toast.error('Please enter a ticket ID');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let ticketId = ticketIdOrQr.trim().toUpperCase();
      if (ticketId.includes('/ticket/')) {
        ticketId = ticketId.split('/ticket/').pop().split('?')[0];
      }

      const response = await axios.post(
        `${API_URL}/api/tickets/validate`,
        { ticket_id: ticketId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Map backend status to frontend format
      const backendStatus = response.data.status?.toUpperCase();
      const isValid = backendStatus === 'VALID';
      const isUsed = backendStatus === 'ALREADY_USED';

      const validationResult = {
        ...response.data,
        valid: isValid,
        status: isUsed ? 'used' : (isValid ? 'valid' : 'invalid'),
        ticketId,
        timestamp: new Date().toLocaleTimeString()
      };

      setResult(validationResult);
      setRecentValidations(prev => [validationResult, ...prev.slice(0, 9)]);
      
      if (isValid) {
        toast.success('VALID - Entry Allowed!');
      } else if (isUsed) {
        toast.warning('Ticket already used');
      } else {
        toast.error(response.data.message || 'Invalid ticket');
      }
    } catch (error) {
      const errorResult = {
        valid: false,
        status: 'invalid',
        message: error.response?.data?.detail || error.response?.data?.message || 'Failed to validate ticket',
        ticketId: ticketIdOrQr,
        timestamp: new Date().toLocaleTimeString()
      };
      setResult(errorResult);
      setRecentValidations(prev => [errorResult, ...prev.slice(0, 9)]);
      toast.error(errorResult.message);
    } finally {
      setLoading(false);
      setTicketInput('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleValidate(ticketInput);
  };

  const resetValidation = () => {
    setResult(null);
    setTicketInput('');
    if (mode === 'camera') {
      setTimeout(() => startScanner(), 100);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Ticket Validation</h1>
          <p className="text-muted-foreground">Scan QR code or enter ticket ID manually</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Validation Panel */}
          <div className="space-y-4">
            {/* Mode Selector */}
            <div className="flex gap-2">
              <Button
                variant={mode === 'manual' ? 'default' : 'outline'}
                onClick={() => {
                  stopScanner();
                  setMode('manual');
                  setResult(null);
                }}
                className={`flex-1 ${mode === 'manual' ? 'bg-[#0055FF]' : ''}`}
              >
                <Keyboard className="w-4 h-4 mr-2" />
                Enter Code
              </Button>
              <Button
                variant={mode === 'camera' ? 'default' : 'outline'}
                onClick={() => {
                  setMode('camera');
                  setResult(null);
                  setTimeout(() => startScanner(), 100);
                }}
                className={`flex-1 ${mode === 'camera' ? 'bg-[#0055FF]' : ''}`}
              >
                <Camera className="w-4 h-4 mr-2" />
                Scan QR
              </Button>
            </div>

            {/* Validation Result */}
            {result && (
              <Card className={`border-2 ${
                result.valid 
                  ? 'border-green-500 bg-green-50' 
                  : result.status === 'used'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-red-500 bg-red-50'
              }`}>
                <CardContent className="pt-6 text-center">
                  <div className="mb-4">
                    {result.valid ? (
                      <CheckCircle className="w-20 h-20 text-green-500 mx-auto" />
                    ) : result.status === 'used' ? (
                      <AlertTriangle className="w-20 h-20 text-yellow-500 mx-auto" />
                    ) : (
                      <XCircle className="w-20 h-20 text-red-500 mx-auto" />
                    )}
                  </div>

                  <h2 className={`text-2xl font-bold mb-2 ${
                    result.valid ? 'text-green-700' : result.status === 'used' ? 'text-yellow-700' : 'text-red-700'
                  }`}>
                    {result.valid ? 'VALID - ENTRY ALLOWED' : result.status === 'used' ? 'ALREADY USED' : 'INVALID TICKET'}
                  </h2>

                  {result.message && (
                    <p className="text-muted-foreground mb-4">{result.message}</p>
                  )}

                  {result.ticket && (
                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm text-left mb-4">
                      <div className="flex items-center gap-2">
                        <Ticket className="w-4 h-4 text-[#0055FF]" />
                        <span className="font-mono font-bold">{result.ticket.ticket_id || result.ticketId}</span>
                      </div>
                      {result.ticket.event_name && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{result.ticket.event_name}</span>
                        </div>
                      )}
                      {result.ticket.category && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4" />
                          <span>{result.ticket.category}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Button 
                    onClick={resetValidation}
                    className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Scan Next Ticket
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Manual Entry */}
            {mode === 'manual' && !result && (
              <Card>
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-2">Enter Ticket ID</label>
                      <Input
                        placeholder="TKT-123456"
                        value={ticketInput}
                        onChange={(e) => setTicketInput(e.target.value.toUpperCase())}
                        className="h-14 text-xl font-mono text-center"
                        autoFocus
                        autoComplete="off"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full h-14 text-lg bg-[#0055FF] hover:bg-[#0044CC]"
                      disabled={loading || !ticketInput.trim()}
                    >
                      {loading ? 'Validating...' : 'Validate Ticket'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Camera Scanner */}
            {mode === 'camera' && !result && (
              <Card className="overflow-hidden">
                <CardContent className="p-0">
                  <div 
                    id="admin-qr-reader" 
                    className="w-full aspect-square bg-gray-100"
                  />
                  {!scanning && (
                    <div className="p-4">
                      <Button 
                        onClick={startScanner}
                        className="w-full bg-[#0055FF] hover:bg-[#0044CC]"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Start Camera
                      </Button>
                    </div>
                  )}
                  {scanning && (
                    <p className="text-center text-sm text-muted-foreground p-4">
                      Point camera at QR code...
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recent Validations */}
          <div>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Recent Validations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentValidations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No validations yet. Enter a ticket ID or scan a QR code.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {recentValidations.map((v, i) => (
                      <div 
                        key={i}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          v.valid 
                            ? 'bg-green-50 border border-green-200' 
                            : v.status === 'used'
                              ? 'bg-yellow-50 border border-yellow-200'
                              : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {v.valid ? (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : v.status === 'used' ? (
                            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-mono font-medium text-sm">{v.ticketId}</p>
                            <p className="text-xs text-muted-foreground">{v.timestamp}</p>
                          </div>
                        </div>
                        <Badge 
                          variant={v.valid ? 'default' : 'secondary'}
                          className={`text-xs ${
                            v.valid ? 'bg-green-500' : v.status === 'used' ? 'bg-yellow-500' : 'bg-red-500'
                          } text-white`}
                        >
                          {v.valid ? 'Valid' : v.status === 'used' ? 'Used' : 'Invalid'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
